/**
 * User Module Types (Domain)
 *
 * Core user types and entities.
 */

// ============================================================================
// Branded Types
// ============================================================================

declare const __brand: unique symbol;
type Brand<T, B> = T & { [__brand]: B };

export type UserId = Brand<string, 'UserId'>;

// ============================================================================
// Entities
// ============================================================================

/**
 * User entity
 */
export interface User {
  id: UserId;
  authenticatedId?: string;
  email: string;
  name: string;
  displayName?: string;
  avatar?: string;
  photoUrl?: string;
  role: UserRole;
  extensionNumber?: string;
  extensionStatus?: string;
  extensionType?: string;
  mobileNumber?: string;
  status?: string;
  createdAt?: string;
  lastLoginAt?: string;
  /** Backend-encrypted SIP password (AES-GCM); decrypt with config.webrtc.symmetricEncryptionKey */
  sipPasswordEncrypted?: string;
}

/**
 * User roles for authorization
 */
export enum UserRole {
  Admin = 'ADMIN',
  Agent = 'AGENT',
  Supervisor = 'SUPERVISOR',
  Viewer = 'VIEWER',
  SystemAdmin = 'System Admin',
  Manager = 'Manager',
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * User API response (matches Flutter backend)
 */
export interface UserApiResponse {
  authenticatedId: string;
  displayName: string;
  extension?: string;
  extension_status?: string;
  extension_type?: string;
  role?: string;
  photo_url?: string;
  email: string;
  mobileNumber?: string;
  status?: string;
  work_duration?: number;
  sip_password?: string;
}

/**
 * User profile response
 */
export interface UserResponse {
  user: User;
}

/**
 * Test call response from POST /user/test-call
 */
export interface TestCallResponse {
  message: string;
  channel_id: string;
}

/**
 * Cache action request body for POST /cache
 */
export interface ClearCacheRequest {
  action: 'clear_all' | 'clear_user' | 'get_user_stats' | 'reset_metrics';
  user_id?: number;
}

/**
 * Cache action response from POST /cache
 */
export interface ClearCacheResponse {
  status: string;
  message: string;
}
